import { tracking } from '@iwoca/frontend-tracking-library';
import { IconInfoCircleOutline } from '@iwoca/icons';
import { Button, Card } from '@iwoca/orion';

import { SecurityChecksListStep } from './SecurityChecksListStep/SecurityChecksListStep';
import { useOnce } from '../../../../hooks/useOnce';
import { LoadingCard } from '../../../components/LoadingCard/LoadingCard';
import { WhiteLabelOverride } from '../../../components/WhiteLabelOverride/WhiteLabelOverride';
import { useCurrentOfferId } from '../../hooks/useCurrentOfferId';
import { useNonLinearSecurityChecks } from '../../hooks/useNonLinearSecurityChecks.hook';

export const SecurityChecksList = () => {
  const {
    steps,
    areStepsCompleted,
    loadingNonLinearSecurityChecks,
    goToOfferOrGiveUsACall,
    nextStepName,
  } = useNonLinearSecurityChecks();
  const offerId = useCurrentOfferId();

  const isMajorShareholdersStepVisible = steps['major-shareholders'].visible;

  useOnce({
    conditionFn: !loadingNonLinearSecurityChecks && offerId !== null,
    fn: () =>
      tracking.ampli.nonLinearSecurityChecksLandingVisited({
        offer_id: offerId!,
      }),
  });

  if (loadingNonLinearSecurityChecks) return <LoadingCard />;

  const completeSecurityChecks = () => {
    // Fire when security steps complete
    tracking.ampli.nonLinearSecurityChecksOfferPageReached({
      offer_id: offerId!,
    });
    goToOfferOrGiveUsACall();
  };

  return (
    <Card data-testid="non-linear-security-checks">
      <div className="w-full">
        <div className="gap-l px-s">
          {areStepsCompleted ? (
            <>
              <h1 className="!text-2xl">Summary</h1>
              <p className="text-m">Are all your details correct?</p>
            </>
          ) : (
            <>
              <h1 className="!text-2xl">One-time security checks</h1>
              <p className="text-m">
                You can complete the steps below in any order
              </p>
            </>
          )}
        </div>
        {!areStepsCompleted && (
          <div className="mx-s flex items-center gap-l bg-secondary-95 px-s py-l">
            <IconInfoCircleOutline className="size-xl" />
            <div>
              <p className="m-0 text-xs font-reg">
                To complete some steps you'll need verification documents at
                hand
              </p>
              <ul className="m-0 pl-xl text-xs font-reg">
                <li>Passport or driving license</li>
                {isMajorShareholdersStepVisible && (
                  <li>
                    The date of birth, address, and full name of your company's
                    shareholders
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}
        <div className="mx-s my-l divide-y divide-secondary-90 border-b border-t border-secondary-90">
          {Object.entries(steps)
            .sort(([, a], [, b]) =>
              a.completed === b.completed ? 0 : a.completed ? 1 : -1,
            )
            .map(
              ([stepName, step]) =>
                step.visible && (
                  <SecurityChecksListStep
                    step={step}
                    key={stepName}
                    stepName={stepName}
                  />
                ),
            )}
        </div>
        {areStepsCompleted && (
          <WhiteLabelOverride componentType="button" disabled={false}>
            <Button
              data-testid={nextStepName}
              className="mx-s"
              onClick={completeSecurityChecks}
            >
              Confirm and continue
            </Button>
          </WhiteLabelOverride>
        )}
      </div>
    </Card>
  );
};
